import React, { useState, useEffect, useRef } from "react";
import EditTemplateModal from "./EditTemplateModal";
import EditSpecialTemplateModal from "../AdditionalInvoiceType/EditSpecialTemplateModal";

const TemplateSection = ({
  templates,
  handleTemplateClick,
  handleOpenTemplate,
  isModalOpen,
  selectedTemplate,
  setIsModalOpen,
  handleSaveTemplate,
  handleDeleteTemplate,
  handleSpecialReceipt,
  category,
  setNavbarButtons,
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const dropdownRefs = useRef({});
  const carouselRef = useRef(null);

  useEffect(() => {
    if (category === "invoices") {
      setNavbarButtons([
        {
          label: "New Invoice Template",
          icon: "add",
          onClick: () => handleOpenTemplate(null),
        },
      ]);
    } else if (category === "noc") {
      setNavbarButtons([
        {
          label: "New Notice of Charges Template",
          icon: "add",
          onClick: () => handleOpenTemplate(null),
        },
      ]);
    } else if (category === "transmittal") {
      setNavbarButtons([
        {
          label: "New Transmittal Letter Template",
          icon: "add",
          onClick: () => handleSpecialReceipt(null),
        },
      ]);
    } else if (category === "acknowledgement") {
      setNavbarButtons([
        {
          label: "New Acknowledgement Receipt Template",
          icon: "add",
          onClick: () => handleSpecialReceipt(null),
        },
      ]);
    } else if (category === "sec-res") {
      setNavbarButtons([
        {
          label: "New Secretarial Resolutions Template",
          icon: "add",
          onClick: () => handleSpecialReceipt(null),
        },
      ]);
    } else if (category === "legal-advice") {
      setNavbarButtons([
        {
          label: "New Legal Advice Template",
          icon: "add",
          onClick: () => handleSpecialReceipt(null),
        },
      ]);
    } else {
      setNavbarButtons([]); // nothing
    }
  }, [category, setNavbarButtons]);

  const filteredTemplates = (() => {
    if (category === "noc") {
      return templates.filter((t) => t.tempType === "noc");
    } else if (category === "transmittal") {
      return templates.filter((t) => t.tempType === "trans");
    } else if (category === "acknowledgement") {
      return templates.filter((t) => t.tempType === "ack");
    } else if (category === "sec-res") {
      return templates.filter((t) => t.tempType === "secres");
    } else if (category === "legal-advice") {
      return templates.filter((t) => t.tempType === "legal");
    } else {
      // invoices
      return templates.filter((t) => !t.tempType || t.tempType === "inv");
    }
  })();

  // Check scroll position to show/hide arrows
  useEffect(() => {
    const checkScroll = () => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener("scroll", checkScroll);
      checkScroll(); // Initial check
    }

    return () => {
      if (carousel) {
        carousel.removeEventListener("scroll", checkScroll);
      }
    };
  }, [filteredTemplates]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown !== null) {
        const ref = dropdownRefs.current[openDropdown];
        if (ref && !ref.contains(event.target)) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  const toggleDropdown = (templateId, e) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === templateId ? null : templateId);
  };

  const handleEdit = (template, e) => {
    e.stopPropagation();
    setOpenDropdown(null);

    // console.log(category);

    handleOpenTemplate(template);
  };

  const handleDeleteClick = (templateId, e) => {
    e.stopPropagation();
    setOpenDropdown(null);
    handleDeleteTemplate(templateId);
  };

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 200; // Adjust based on card width
      const currentScroll = carouselRef.current.scrollLeft;
      const newScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      carouselRef.current.scrollTo({
        left: newScroll,
        behavior: "smooth",
      });
    }
  };

  const renderTemplateButton = (template) => {
    const isOpen = openDropdown === template.id;

    return (
      <div key={template.id} className="flex-shrink-0 relative group">
        {/* Main Template Card */}
        <div
          onClick={() => handleTemplateClick(template)}
          className="w-52 bg-white/10 dark:bg-white/10 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 dark:border-gray-600 hover:border-tier-secondary dark:hover:border-dark-alt hover:-translate-y-1 overflow-hidden"
        >
          {/* Header Section */}
          <div className="relative p-4 bg-gradient-to-br from-teal-50 to-amber-100 dark:from-tier-primary dark:to-tier-primary-dark">
            {/* Menu Button */}
            <div
              ref={(el) => (dropdownRefs.current[template.id] = el)}
              className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <button
                onClick={(e) => toggleDropdown(template.id, e)}
                className="p-1.5 rounded-full bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-900 transition-colors shadow-sm"
                title="More options"
              >
                <span className="material-symbols-outlined text-lg text-gray-700 dark:text-gray-300">
                  more_vert
                </span>
              </button>

              {/* Dropdown Menu */}
              {isOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-tier-primary dark:bg-tier-primary-dark rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-2 z-20 animate-in slide-in-from-top-2 duration-200">
                  <button
                    onClick={(e) => handleEdit(template, e)}
                    className="w-full px-4 py-2 text-left text-sm text-color hover:text-tier-primary-dark dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
                  >
                    <span className="material-symbols-outlined text-base text-tier-secondary dark:text-dark-alt">
                      edit
                    </span>
                    Edit Template
                  </button>
                  <button
                    onClick={(e) => handleDeleteClick(template.id, e)}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 transition-colors"
                  >
                    <span className="material-symbols-outlined text-base">
                      delete_outline
                    </span>
                    Delete
                  </button>
                </div>
              )}
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-3">
              <div className="w-12 h-12 bg-tier-secondary dark:bg-dark-alt rounded-full flex items-center justify-center shadow-lg">
                <span className="material-symbols-outlined text-white dark:text-tier-primary text-2xl">
                  {category === "acknowledgement"
                    ? "receipt"
                    : category === "transmittal"
                    ? "send"
                    : "receipt_long"}
                </span>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-4 space-y-3">
            {/* Template Name */}
            <h3 className="font-semibold text-color text-center line-clamp-2 leading-tight">
              {template.header}
            </h3>

            {/* From Info */}
            <div className="flex items-center justify-center gap-2 text-sm text-alt-color">
              <span className="material-symbols-outlined text-base">
                person
              </span>
              <span className="truncate">{template.from.name}</span>
            </div>
          </div>

          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-blue-500/5 dark:bg-blue-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        </div>

        {/* Modal for regular templates (invoices/noc) */}
        {isModalOpen &&
          selectedTemplate?.id === template.id &&
          (category === "invoices" || category === "noc") && (
            <EditTemplateModal
              key={`modal-${template.id}`}
              template={selectedTemplate}
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSave={handleSaveTemplate}
              invoiceCategory={category}
            />
          )}

        {/* Modal for special templates (acknowledgement/transmittal) */}
        {isModalOpen &&
          selectedTemplate?.id === template.id &&
          (category === "acknowledgement" ||
            category === "transmittal" ||
            category === "sec-res" ||
            category === "legal-advice") && (
            <EditSpecialTemplateModal
              key={`special-modal-${template.id}`}
              template={selectedTemplate}
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSave={handleSaveTemplate}
              templateType={
                category === "acknowledgement"
                  ? "ack"
                  : category === "transmittal"
                  ? "trans"
                  : category === "sec-res"
                  ? "secres"
                  : "legal"
              }
            />
          )}
      </div>
    );
  };

  const getTemplateTypeLabel = () => {
    switch (category) {
      case "invoices":
        return "Invoice";
      case "noc":
        return "Notice of Charges";
      case "transmittal":
        return "Transmittal Letter";
      case "acknowledgement":
        return "Acknowledgement Receipt";
      case "sec-res":
        return "Secretarial Resolution";
      case "legal-advice":
        return "Legal Advice";
      default:
        return "Template";
    }
  };

  // Show template grid for all supported categories
  const shouldShowTemplateGrid = [
    "invoices",
    "noc",
    "transmittal",
    "acknowledgement",
    "sec-res",
    "legal-advice",
  ].includes(category);

  return shouldShowTemplateGrid ? (
    <div className="w-full py-6">
      <div className="px-4 relative max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-color">
              {getTemplateTypeLabel()} Templates
            </h2>
          </div>
          <div className="text-xs text-dark-alt bg-tier-secondary/20 dark:bg-dark-alt/20 px-3 py-1 rounded-full">
            {filteredTemplates.length} template
            {filteredTemplates.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scrollCarousel("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-icon-color dark:bg-tier-secondary border border-gray-200 dark:border-gray-600 rounded-full px-2 pt-1 shadow-lg hover:shadow-xl shadow-icon-color transition-all duration-200 hover:scale-110"
              aria-label="Scroll left"
            >
              <span className="material-symbols-outlined text-gray-700 dark:text-tier-primary text-xl">
                chevron_left
              </span>
            </button>
          )}

          {/* Templates Grid/Carousel */}
          <div
            ref={carouselRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-12 py-4"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {filteredTemplates.map(renderTemplateButton)}
          </div>

          {/* Right Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scrollCarousel("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-icon-color dark:bg-tier-secondary border border-gray-200 dark:border-gray-600 rounded-full px-2 pt-1 shadow-lg hover:shadow-xl shadow-icon-color transition-all duration-200 hover:scale-110"
              aria-label="Scroll right"
            >
              <span className="material-symbols-outlined text-gray-700 dark:text-tier-primary text-xl">
                chevron_right
              </span>
            </button>
          )}
        </div>

        {/* New Template Modal for regular templates */}
        {isModalOpen &&
          selectedTemplate === null &&
          (category === "invoices" || category === "noc") && (
            <EditTemplateModal
              key="new-template"
              template={null}
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSave={handleSaveTemplate}
              invoiceCategory={category}
            />
          )}

        {/* New Template Modal for special templates */}
        {isModalOpen &&
          selectedTemplate === null &&
          (category === "acknowledgement" ||
            category === "transmittal" ||
            category === "sec-res" ||
            category === "legal-advice") && (
            <EditSpecialTemplateModal
              key="new-special-template"
              template={null}
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSave={handleSaveTemplate}
              templateType={
                category === "acknowledgement"
                  ? "ack"
                  : category === "transmittal"
                  ? "trans"
                  : category === "sec-res"
                  ? "secres"
                  : "legal"
              }
            />
          )}
      </div>

      {/* Custom scrollbar styles */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  ) : (
    <div className="w-full px-2 sm:px-6 md:px-8">
      <div className="text-sm text-alt-color">
        {/* Fallback for any unsupported categories */}
      </div>
    </div>
  );
};

export default TemplateSection;
